/**
 * Consent Table Component
 *
 * Renders consent records with actionable controls for FI data,
 * logs, refresh, SMS, and analytics. Integrates with Equal DS table
 * while honouring permissions derived from user roles.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Download,
  Eye,
  GitPullRequestArrow,
  ListPlus,
  RefreshCw,
  CreditCard,
} from 'lucide-react';
import { Table, Button, SearchBar, Tag, type TableColumn } from 'equal-ds-ui';
import { PAGE_SIZE_OPTIONS } from '../../../constants/pagination.constants';
import type {
  ConsentTableProps,
  ConsentTableRow,
  ConsentRequestRecord,
  ConsentSearchDomain,
} from '../../../types/consent';


/**
 * Consent Status Colors - Using Equal DS finpro-tags.css classes
 * These map to pre-defined CSS classes in the design system
 */
const CONSENT_STATUS_COLORS: Record<string, { label: string; className: string }> = {
  // Active/Success states
  active: { label: 'ACTIVE', className: 'status-active' },
  approved: { label: 'APPROVED', className: 'status-active' },
  delivered: { label: 'DELIVERED', className: 'status-active' },
  ready: { label: 'READY', className: 'status-active' },
  // Pending/Processing states
  pending: { label: 'PENDING', className: 'status-pending' },
  pending_from_fip: { label: 'PENDING FROM FIP', className: 'status-pending' },
  pending_from_aa: { label: 'PENDING FROM AA', className: 'status-pending' },
  pending_from_fiu: { label: 'PENDING FROM FIU', className: 'status-pending' },
  awaiting: { label: 'AWAITING', className: 'status-pending' },
  processing: { label: 'PROCESSING', className: 'status-pending' },
  initiated: { label: 'INITIATED', className: 'status-pending' },
  data_ready: { label: 'DATA READY', className: 'status-paused' },
  // Error/Rejected states
  failed: { label: 'FAILED', className: 'status-failed' },
  denied: { label: 'DENIED', className: 'status-rejected' },
  rejected: { label: 'REJECTED', className: 'status-rejected' },
  error: { label: 'ERROR', className: 'status-rejected' },
  // Revoked/Timeout states
  revoked: { label: 'REVOKED', className: 'status-revoked' },
  timeout: { label: 'TIMEOUT', className: 'status-revoked' },
  timed_out: { label: 'TIMED OUT', className: 'status-revoked' },
  // Paused/Expired states
  paused: { label: 'PAUSED', className: 'status-paused' },
  expired: { label: 'EXPIRED', className: 'status-expired' },
};

const deriveConsentStatusTag = (status?: string | null) => {
  if (!status) {
    return status;
  }
  const normalized = String(status).toLowerCase();
  const statusConfig = CONSENT_STATUS_COLORS[normalized];
  // Use Equal DS status class or fallback to status-expired (neutral gray)
  const className = statusConfig?.className ?? 'status-expired';
  return {
    type: 'tag' as const,
    props: {
      children: statusConfig?.label ?? status.toUpperCase(),
      className,
    },
  };
};

const deriveDataStatus = (record: ConsentRequestRecord): string | null => {
  const fetchStatus =
    record.consent_data_fetches && record.consent_data_fetches.length > 0
      ? record.consent_data_fetches[0]?.data_life_status
      : null;

  if (fetchStatus) {
    return fetchStatus;
  }

  const requestStatus =
    record.consent_data_requests && record.consent_data_requests.length > 0
      ? record.consent_data_requests[0]?.status
      : null;

  return requestStatus ?? null;
};

const deriveDataStatusTag = (record: ConsentRequestRecord) => {
  const status = deriveDataStatus(record);
  const normalized = status?.toLowerCase();

  // Only 3 states: AVAILABLE, PURGED, UNAVAILABLE
  if (normalized === 'active') {
    return {
      type: 'tag' as const,
      props: {
        children: 'AVAILABLE',
        className: 'status-active',
      },
    };
  }

  if (normalized === 'purged') {
    return {
      type: 'tag' as const,
      props: {
        children: 'PURGED',
        className: 'status-revoked',
      },
    };
  }

  // Everything else (ARCHIVED, EXPIRED, FAILED, PENDING, etc. or no data) → UNAVAILABLE
  return {
    type: 'tag' as const,
    props: {
      children: 'UNAVAILABLE',
      className: 'status-failed',
    },
  };
};

const deriveFiDataRange = (record: ConsentRequestRecord): string => {
  const from = record.fi_data_range_from ?? record.consent_start ?? '';
  const to = record.fi_data_range_to ?? record.consent_expiry ?? '';
  if (!from && !to) {
    return '-';
  }
  if (!from) {
    return `Till ${to}`;
  }
  if (!to) {
    return `From ${from}`;
  }
  return `${from} → ${to}`;
};

const DEFAULT_SEARCH_DOMAIN: ConsentSearchDomain = 'customer_vua';

export const ConsentTable: React.FC<ConsentTableProps> = ({
  data,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  onViewLogs,
  onRefreshRow,
  onRefreshPage,
  onOpenDownloadOptions,
  onViewFiData,
  onViewAccountDetails,
  onTriggerFiRequest,
  onSendSms: _onSendSms,
  onSearch,
  currentSearch,
  isLoading = false,
  permissions,
  smsEnabled: _smsEnabled,
  onCreateConsent,
  onDownloadCsv,
}) => {
  const [refreshingRows, setRefreshingRows] = useState<Set<string>>(new Set());
  const [searchDomain, setSearchDomain] = useState<ConsentSearchDomain>(
    currentSearch?.domain ?? DEFAULT_SEARCH_DOMAIN
  );
  const [searchQuery, setSearchQuery] = useState(currentSearch?.query ?? '');

  useEffect(() => {
    if (!currentSearch) {
      setSearchDomain(DEFAULT_SEARCH_DOMAIN);
      setSearchQuery('');
      return;
    }
    setSearchDomain(currentSearch.domain);
    setSearchQuery(currentSearch.query);
  }, [currentSearch]);

  useEffect(() => {
    if (!onSearch) {
      return;
    }
    const handler = window.setTimeout(() => {
      onSearch(searchDomain, searchQuery);
    }, 400);
    return () => window.clearTimeout(handler);
  }, [searchDomain, searchQuery, onSearch]);

  const handleRefreshRow = async (row: ConsentTableRow) => {
    if (!onRefreshRow) {
      return;
    }
    const rowId = row._rowId;
    setRefreshingRows((prev) => new Set(prev).add(rowId));
    try {
      await onRefreshRow(row);
    } finally {
      setRefreshingRows((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
    }
  };

  const columns: TableColumn<ConsentTableRow>[] = useMemo(() => {
    const list: TableColumn<ConsentTableRow>[] = [
      {
        key: 'fiData',
        header: 'FI Data',
        width: 240,
        locked: true,
        render: (_value, row) => {
          // Match Angular's getButtonDisabled logic:
          // Button enabled when: consent_data_fetches.length > 0 AND status !== 'REVOKED' AND data_life_status === 'ACTIVE'
          const dataFetches = row.consent_data_fetches ?? [];
          const hasDataFetches = dataFetches.length > 0;
          const isNotRevoked = row.status !== 'REVOKED';
          const dataLifeStatusActive = hasDataFetches && dataFetches[0]?.data_life_status === 'ACTIVE';
          const isButtonDisabled = !(hasDataFetches && isNotRevoked && dataLifeStatusActive);

          return (
            <div className="flex flex-col gap-2">
              {permissions?.canViewFiData && onViewFiData ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onViewFiData(row)}
                  disabled={isButtonDisabled}
                  className="justify-start"
                >
                  <Eye className="w-4 h-4" />
                  View Data
                </Button>
              ) : null}
              {permissions?.canDownloadFiData && onOpenDownloadOptions ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onOpenDownloadOptions(row, 'download')}
                  disabled={isButtonDisabled}
                  className="justify-start"
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'accountDetails',
        header: 'Customer Accounts',
        width: 180,
        render: (_value, row) => {
          // Account Details button - enabled when consent is not PENDING/REJECTED/FAILED
          // This follows Angular's logic: disabled when status === 'PENDING' || status === 'REJECTED' || status === 'Failed'
          const statusUpper = String(row.status ?? '').toUpperCase();
          const isButtonDisabled =
            statusUpper === 'PENDING' || statusUpper === 'REJECTED' || statusUpper === 'FAILED';

          // Only show if permission is granted and handler is provided
          if (!permissions?.canViewAccountDetails || !onViewAccountDetails) {
            return null;
          }

          return (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onViewAccountDetails(row)}
              disabled={isButtonDisabled}
              className="justify-start"
            >
              <CreditCard className="w-4 h-4" />
              Account Details
            </Button>
          );
        },
      },
      {
        key: 'dataRequestMode',
        header: 'Data Request Mode',
        width: 200,
        render: (_value, row) => {
          const fiFetch = row.fi_fetch ?? '';
          const fiFetchUpper = String(fiFetch).toUpperCase();
          const isManualOrAutoManual = fiFetchUpper === 'MANUAL' || fiFetchUpper === 'AUTOMANUAL';
          const statusUpper = String(row.status).toUpperCase();
          const isActiveStatus = ['ACTIVE', 'APPROVED'].includes(statusUpper);

          // Show "AUTOMATIC" badge when fi_fetch is not MANUAL or AUTOMANUAL
          if (!isManualOrAutoManual && fiFetch) {
            return (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-blue-500">
                AUTOMATIC
              </span>
            );
          }

          // Show FI Request button for MANUAL/AUTOMANUAL - enabled only when status is ACTIVE/APPROVED
          if (isManualOrAutoManual && permissions?.canTriggerFiRequest) {
            const isButtonDisabled = !isActiveStatus;
            return (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onTriggerFiRequest?.(row)}
                disabled={isButtonDisabled}
                className="justify-start"
              >
                <GitPullRequestArrow className="w-4 h-4" />
                FI Request
              </Button>
            );
          }

          return null;
        },
      },
      {
        key: 'dataStatus',
        header: 'Data Status',
        width: 180,
        accessor: (row) => deriveDataStatusTag(row),
      },
      {
        key: 'status',
        header: 'Consent Status',
        width: 220,
        render: (_value, row) => {
          const statusTag = deriveConsentStatusTag(row.status);
          const isPending = row.status?.toUpperCase() === 'PENDING';
          const isRefreshing = refreshingRows.has(row._rowId);

          return (
            <div className="flex items-center gap-2">
              {statusTag && typeof statusTag === 'object' && 'type' in statusTag ? (
                <Tag size="sm" className={statusTag.props.className}>
                  {statusTag.props.children}
                </Tag>
              ) : (
                <span>{row.status ?? '-'}</span>
              )}
              {isPending && onRefreshRow && (
                <button
                  onClick={() => handleRefreshRow(row)}
                  disabled={isRefreshing}
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                  title="Refresh consent status"
                >
                  <RefreshCw className={`w-4 h-4 text-blue-600 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              )}
            </div>
          );
        },
      },
      {
        key: 'account_id',
        header: 'Account ID',
        width: 180,
        accessor: (row) => row.account_id ?? '-',
      },
      {
        key: 'customer_vua',
        header: 'Customer VUA',
        width: 200,
        accessor: (row) => row.customer_vua ?? '-',
        copyable: true,
      },
      {
        key: 'mobile_no',
        header: 'Mobile Number',
        width: 160,
        accessor: (row) => row.mobile_no ?? '-',
      },
      {
        key: 'consent_handle',
        header: 'Consent Handle',
        width: 200,
        accessor: (row) => row.consent_handle ?? '-',
        copyable: true,
      },
      {
        key: 'consent_id',
        header: 'Consent ID',
        width: 200,
        accessor: (row) => row.consent_id ?? '-',
        copyable: true,
      },
      {
        key: 'product_id',
        header: 'Template Code',
        width: 180,
        accessor: (row) => row.product_id ?? '-',
      },
      {
        key: 'purpose',
        header: 'Purpose',
        width: 240,
        accessor: (row) => row.purpose_text ?? row.purpose_code ?? '-',
      },
      {
        key: 'fi_data_range',
        header: 'FI Data Range',
        width: 200,
        accessor: (row) => deriveFiDataRange(row),
      },
      {
        key: 'fetch_type',
        header: 'Fetch Type',
        width: 140,
        accessor: (row) => row.fetch_type ?? '-',
      },
      {
        key: 'aa_id',
        header: 'AA ID',
        width: 160,
        accessor: (row) => row.aa_id ?? '-',
      },
      {
        key: 'createdAt',
        header: 'Created On',
        width: 180,
        accessor: (row) => row.createdAt ?? '-',
      },
      {
        key: 'empDetails',
        header: 'Employee',
        width: 200,
        accessor: (row) => {
          const name = row.name ?? row.user_profile?.name ?? '-';
          const empId = row.emp_id ?? row.user_profile?.emp_id ?? '';
          if (!empId) return name;
          return `${name} (${empId})`;
        },
      },
      {
        key: 'actions',
        header: 'Actions',
        width: 140,
        align: 'center',
        isActions: true,
        render: (_value, row) => {
          if (!permissions?.canViewLogs) {
            return <span className="text-xs text-text-tertiary">-</span>;
          }

          return (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onViewLogs(row, 'consent')}
              className="justify-center"
            >
              <Eye className="w-4 h-4" />
              View Logs
            </Button>
          );
        },
      },
    ];

    // Filter out columns based on RBAC permissions (matching Angular behavior)
    return list.filter((col) => {
      // Hide FI Data column if user lacks both view and download permissions
      if (col.key === 'fiData') {
        return permissions?.canViewFiData || permissions?.canDownloadFiData;
      }
      // Hide Customer Accounts column if user lacks permission
      if (col.key === 'accountDetails') {
        return permissions?.canViewAccountDetails;
      }
      // Hide Actions/View Logs column if user lacks permission
      if (col.key === 'actions') {
        return permissions?.canViewLogs;
      }
      // Hide Data Request Mode column if user lacks FI Request permission
      if (col.key === 'dataRequestMode') {
        return permissions?.canTriggerFiRequest;
      }
      return true;
    });
  }, [onOpenDownloadOptions, onViewFiData, onViewAccountDetails, onTriggerFiRequest, onViewLogs, permissions, onRefreshRow, refreshingRows, handleRefreshRow]);

  return (
    <div className="flex-1 min-h-0 flex flex-col p-4 border border-border-default rounded-xl shadow-lg bg-white">
      {/* Header with Search and Actions */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1">
          <SearchBar
            variant="with-dropdown"
            domains={[
              { value: 'customer_vua', label: 'Customer VUA' },
              { value: 'consent_id', label: 'Consent ID' },
              { value: 'consent_handle', label: 'Consent Handle' },
              { value: 'account_id', label: 'Account ID' },
              { value: 'mobile_no', label: 'Mobile Number' },
              { value: 'data_delivered', label: 'Data Status' },
            ]}
            selectedDomain={searchDomain}
            onDomainChange={(value) => setSearchDomain(value as ConsentSearchDomain)}
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search consents..."
          />
        </div>

        <div className="flex items-center gap-3">
          {onRefreshPage ? (
            <Button
              variant="secondary"
              size="md"
              onClick={onRefreshPage}
              disabled={isLoading}
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          ) : null}

          {onCreateConsent ? (
            <Button variant="primary" size="md" onClick={onCreateConsent}>
              <ListPlus className="w-6 h-6" />
              Create Consent
            </Button>
          ) : null}

          {onDownloadCsv ? (
            <Button variant="secondary" size="md" onClick={onDownloadCsv}>
              <Download className="w-6 h-6" />
              Download
            </Button>
          ) : null}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <Table<ConsentTableRow>
          className="flex-1 min-h-0 shadow-sm rounded-xl"
          columns={columns}
          data={data}
          onRefreshRow={onRefreshRow ? handleRefreshRow : undefined}
          refreshingRows={refreshingRows}
          columnManager={{
            enabled: true,
            triggerLabel: 'Manage columns',
            allowHiding: true,
          }}
          pagination={{
            currentPage,
            pageSize,
            totalItems,
            onPageChange,
            onPageSizeChange,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            showPageSizeSelector: true,
            isFetching: isLoading,
          }}
        />
      </div>
    </div>
  );
};
